// Messages owned by the shared rules (validation, definition checks, type and
// operator labels) in English (US) and Bahasa Indonesia. Keys mirror the Go
// catalogue in api/internal/i18n so client and server say the same thing.
import type { Operator, QuestionType } from './types';

export type Locale = 'en' | 'id';
export const LOCALES: { code: Locale; label: string; tag: string }[] = [
  { code: 'en', label: 'English (US)', tag: 'en-US' },
  { code: 'id', label: 'Bahasa Indonesia', tag: 'id-ID' },
];

/** BCP 47 tag for Intl formatting and Accept-Language. */
export function localeTag(l: Locale): string {
  return l === 'id' ? 'id-ID' : 'en-US';
}

/** Picks a supported locale from a language tag such as navigator.language. */
export function detectLocale(tag: string | undefined | null): Locale {
  const t = (tag ?? '').toLowerCase();
  return t.startsWith('id') || t.startsWith('in') ? 'id' : 'en';
}

const en = {
  'ans.required': 'This question is required.',
  'ans.expected_text': 'Expected text.',
  'ans.too_long': 'Answer is too long.',
  'ans.min_length': 'Answer must be at least {0} characters.',
  'ans.max_length': 'Answer must be at most {0} characters.',
  'ans.choose_one': 'Choose one of the listed options.',
  'ans.expected_list': 'Expected a list of options.',
  'ans.choose_listed': 'Choose from the listed options.',
  'ans.min_selections': 'Select at least {0} options.',
  'ans.max_selections': 'Select at most {0} options.',
  'ans.expected_number': 'Expected a number.',
  'ans.whole_number': 'Expected a whole number.',
  'ans.min': 'Must be at least {0}.',
  'ans.max': 'Must be at most {0}.',
  'ans.rating_range': 'Choose a rating between {0} and {1}.',
  'ans.expected_date': 'Expected a date (YYYY-MM-DD).',
  'ans.valid_date': 'Expected a valid date.',
  'ans.yes_no': 'Answer yes or no.',
  'ans.unsupported': 'Unsupported question type.',
  'ans.specify_other': 'Please specify your “Other” answer.',
  'ans.range': 'Choose a value between {0} and {1}.',
  'ans.step': 'Choose a value in steps of {0}.',
  'ans.every_row': 'Answer every row.',
  'ans.every_field': 'Fill in every field.',
  'ans.non_negative': 'Enter zero or a positive number.',
  'ans.sum_total': 'The values must add up to {0} (now {1}).',
  'ans.bad_file': 'Upload a file.',
  'ans.file_too_big': 'The file must be {0} MB or smaller.',
  'ans.bad_signature': 'Draw your signature.',
  'ans.bad_location': 'Share a valid location.',

  'def.no_questions': 'Add at least one question before publishing.',
  'def.bad_page_mode': 'Page layout must be one question per page, one section per page, or all questions on one page.',
  'def.bad_captcha': 'The human check must be at the start, before submitting, or both.',
  'def.invalid_id': 'Question {0} has an invalid id.',
  'def.duplicate_id': 'Question {0} reuses id "{1}".',
  'def.needs_title': 'Question {0} needs a title.',
  'def.bad_question_code': 'Question {0} has an invalid code. Use 1–32 letters, numbers, hyphens or underscores.',
  'def.duplicate_question_code': 'Question {0} repeats question code "{1}".',
  'def.tooltip_too_long': 'Question {0} has an info tooltip longer than {1} characters.',
  'def.bad_align': 'Question {0} has an unknown text alignment.',
  'def.rich_too_long': 'Question {0} has formatted text longer than {1} characters.',
  'def.needs_two_options': 'Question {0} needs at least two options.',
  'def.bad_option_id': 'Question {0} has a missing or duplicate option id.',
  'def.option_label': 'Question {0} has an option without a label.',
  'def.bad_media': 'Question {0} has an image or video with an invalid link.',
  'def.bad_option_image': 'Question {0} has an option image with an invalid link.',
  'def.rating_scale': 'Question {0} needs a rating scale with 2 to 11 points.',
  'def.min_gt_max': 'Question {0} has a minimum greater than its maximum.',
  'def.minlen_gt_maxlen': 'Question {0} has a minimum length greater than its maximum length.',
  'def.minsel_gt_maxsel': 'Question {0} has minimum selections greater than maximum selections.',
  'def.condition_earlier': 'Question {0} has a condition that must reference an earlier question.',
  'def.bad_operator': 'Question {0} has an unsupported condition operator "{1}".',
  'def.condition_no_value': 'Question {0} has a condition without a value.',
  'def.jump_target_later': 'Question {0} must jump to a later question.',
  'def.bad_match': 'Question {0} has a condition group that must match "all" or "any".',
  'def.needs_field': 'Question {0} needs at least one field.',
  'def.needs_row': 'Question {0} needs at least one row.',
  'def.other_not_allowed': 'Question {0} can only have an “Other” option if it is single choice, multiple choice or dropdown.',
  'def.one_other': 'Question {0} can have only one “Other” option.',
  'def.bad_score': 'Question {0} has an option with invalid points.',
  'def.slider_scale': 'Question {0} needs a slider range with a minimum below the maximum, a positive step, and at most 1000 steps.',
  'def.bad_total': 'Question {0} needs a total greater than zero.',
  'def.bad_carry_forward': 'Question {0} must carry options forward from an earlier single choice, multiple choice or dropdown question.',
  'def.carry_forward_type': 'Question {0} can only carry options forward if it is single choice, multiple choice, dropdown or ranking.',
  'def.bad_condition_row': 'Question {0} has a condition on a row or field that does not exist.',
  'def.jump_ending': 'Question {0} jumps to an ending that does not exist.',
  'def.bad_ending_id': 'An ending has a missing or duplicate id.',
  'def.ending_name': 'Every ending needs a name.',
  'def.ending_redirect': 'Ending “{0}” needs an https link to redirect to.',
  'def.bad_hidden_field': 'Hidden field “{0}” must start with a letter, use only letters, numbers and underscores, and be unique.',
  'randomizer.bad_id': 'A randomizer has a missing or duplicate id.',
  'randomizer.limit': 'Randomizer {0} has an invalid limit for "{1}": use a whole number of 1 or more for one of its sections.',
  'randomizer.reset': 'Randomizer {0} has an invalid reset time.',
  'randomizer.count': 'Randomizer {0} needs at least two sections and must show between 1 and {1} of them.',
  'randomizer.section_missing': 'Randomizer {0} includes the section “{1}”, which does not exist.',
  'randomizer.overlap': 'Section “{1}” is already in another randomizer (randomizer {0}).',
  'randomizer.contiguous': 'The sections in randomizer {0} must follow each other in the survey, with no other section between them.',
  'randomizer.jump': 'Question {0} jumps into or across a randomizer section, so its target may not be shown to every respondent.',
  'randomizer.condition': 'Question {0} depends on a question in a randomizer section that not every respondent sees.',
  'def.bad_loop': 'Section “{0}” must loop over an earlier multiple choice question outside the section.',
  'def.bad_quota_id': 'A quota has a missing or duplicate id.',
  'def.quota_name': 'Every quota needs a name.',
  'def.quota_limit': 'Quota “{0}” needs a limit of at least 1.',
  'def.quota_conditions': 'Quota “{0}” needs valid conditions on questions outside loops.',
  'def.bad_language': '“{0}” is not a valid extra language.',
  'def.bad_color': 'Theme colors must be in #rrggbb form.',
  'def.bad_logo': 'The theme logo has an invalid link.',
  'def.bad_font': 'The theme font is not supported.',
  'def.bad_zoom': 'The theme display size is not supported.',
  'survey.needs_title': 'Give the questionnaire a title.',
  'a11y.image_alt': 'Question {0} has an image without alternative text. Describe it for people using screen readers.',
  'a11y.video_caption': 'Question {0} has a video without a caption or description. Add one, and make sure the video itself has subtitles.',
  'a11y.autoplay_sound': 'Question {0} plays a video with sound automatically. Mute it or turn autoplay off.',
  'a11y.option_image_label': 'Question {0} has picture options without a text label. Give every option a label.',
  'a11y.matrix_wide': 'Question {0} is a matrix with {1} columns, which is hard to use on phones and with screen readers. Keep it to 7 or fewer.',
  'a11y.matrix_long': 'Question {0} is a matrix with {1} statements. Split it into smaller groups.',
  'a11y.ranking_long': 'Question {0} asks to rank more than 10 options, which takes many moves with a keyboard.',
  'a11y.scale_labels': 'Question {0} has a scale without end labels. Say what the lowest and highest values mean.',
  'a11y.many_options': 'Question {0} has more than 15 options. Consider a dropdown or splitting it up.',
  'a11y.vague_link': 'Question {0} says “click here”. Describe where the link goes instead.',
  'a11y.all_caps': 'Question {0} is written in capitals, which is harder to read and may be spelled out by screen readers.',
  'a11y.signature_required': 'Question {0} requires a drawn signature. Some respondents can’t draw one; consider making it optional or offering a typed name.',
  'a11y.location_required': 'Question {0} requires the respondent’s location. Respondents who can’t or won’t share it will be stuck; consider making it optional.',
  'a11y.long_page': 'All {0} questions are on one page. Split long questionnaires into pages or sections.',
  'a11y.timer': 'The elapsed timer can put pressure on respondents. Show it only when time matters.',
  'a11y.accent_contrast': 'The accent color has a contrast of {0}:1 against white. Use a darker color (at least 3:1) so selected answers are visible.',
  'a11y.background_contrast': 'The page background has a contrast of {0}:1 with the text. Use a lighter background (at least 4.5:1).',

  'type.short_text': 'Short text',
  'type.short_text.hint': 'One line answer',
  'type.long_text': 'Long text',
  'type.long_text.hint': 'Paragraph answer',
  'type.single_choice': 'Single choice',
  'type.single_choice.hint': 'Pick one option',
  'type.multiple_choice': 'Multiple choice',
  'type.multiple_choice.hint': 'Pick several options',
  'type.ranking': 'Ranking',
  'type.ranking.hint': 'Order options from most to least preferred',
  'type.dropdown': 'Dropdown',
  'type.dropdown.hint': 'Pick one from a list',
  'type.number': 'Number',
  'type.number.hint': 'Numeric answer',
  'type.rating': 'Rating',
  'type.rating.hint': 'Scale, e.g. 1–5',
  'type.date': 'Date',
  'type.date.hint': 'Calendar date',
  'type.yes_no': 'Yes / No',
  'type.yes_no.hint': 'Binary answer',
  'type.matrix': 'Matrix table',
  'type.matrix.hint': 'Several statements on one scale',
  'type.slider': 'Slider',
  'type.slider.hint': 'Drag to a value',
  'type.nps': 'Net Promoter Score',
  'type.nps.hint': 'Likelihood to recommend, 0–10',
  'type.constant_sum': 'Constant sum',
  'type.constant_sum.hint': 'Split a total between items',
  'type.text_form': 'Text form',
  'type.text_form.hint': 'Several labelled text boxes',
  'type.file_upload': 'File upload',
  'type.file_upload.hint': 'Photo or document',
  'type.signature': 'Signature',
  'type.signature.hint': 'Draw a signature',
  'type.location': 'Location',
  'type.location.hint': 'GPS coordinates',
  'type.descriptive': 'Text / instructions',
  'type.descriptive.hint': 'Information only, no answer',

  'op.equals': 'is',
  'op.not_equals': 'is not',
  'op.contains': 'contains',
  'op.not_contains': 'does not contain',
  'op.gt': 'is greater than',
  'op.gte': 'is at least',
  'op.lt': 'is less than',
  'op.lte': 'is at most',
  'op.answered': 'is answered',
  'op.not_answered': 'is not answered',

  yes: 'Yes',
  no: 'No',
  signed: 'Signed',
};

export type CoreMessageKey = keyof typeof en;

const id: Record<CoreMessageKey, string> = {
  'ans.required': 'Pertanyaan ini wajib dijawab.',
  'ans.expected_text': 'Jawaban harus berupa teks.',
  'ans.too_long': 'Jawaban terlalu panjang.',
  'ans.min_length': 'Jawaban minimal {0} karakter.',
  'ans.max_length': 'Jawaban maksimal {0} karakter.',
  'ans.choose_one': 'Pilih salah satu opsi yang tersedia.',
  'ans.expected_list': 'Jawaban harus berupa daftar opsi.',
  'ans.choose_listed': 'Pilih dari opsi yang tersedia.',
  'ans.min_selections': 'Pilih minimal {0} opsi.',
  'ans.max_selections': 'Pilih maksimal {0} opsi.',
  'ans.expected_number': 'Jawaban harus berupa angka.',
  'ans.whole_number': 'Jawaban harus berupa bilangan bulat.',
  'ans.min': 'Nilai minimal {0}.',
  'ans.max': 'Nilai maksimal {0}.',
  'ans.rating_range': 'Pilih nilai antara {0} dan {1}.',
  'ans.expected_date': 'Jawaban harus berupa tanggal (TTTT-BB-HH).',
  'ans.valid_date': 'Tanggal tidak valid.',
  'ans.yes_no': 'Jawab ya atau tidak.',
  'ans.unsupported': 'Jenis pertanyaan tidak didukung.',
  'ans.specify_other': 'Sebutkan jawaban “Lainnya”.',
  'ans.range': 'Pilih nilai antara {0} dan {1}.',
  'ans.step': 'Pilih nilai dengan kelipatan {0}.',
  'ans.every_row': 'Jawab setiap baris.',
  'ans.every_field': 'Isi setiap kolom.',
  'ans.non_negative': 'Masukkan nol atau angka positif.',
  'ans.sum_total': 'Jumlah nilai harus {0} (saat ini {1}).',
  'ans.bad_file': 'Unggah sebuah berkas.',
  'ans.file_too_big': 'Ukuran berkas maksimal {0} MB.',
  'ans.bad_signature': 'Bubuhkan tanda tangan Anda.',
  'ans.bad_location': 'Bagikan lokasi yang valid.',

  'def.no_questions': 'Tambahkan minimal satu pertanyaan sebelum menerbitkan.',
  'def.bad_page_mode': 'Tata letak halaman harus satu pertanyaan per halaman, satu bagian per halaman, atau semua pertanyaan dalam satu halaman.',
  'def.bad_captcha': 'Verifikasi manusia harus di awal, sebelum mengirim, atau keduanya.',
  'def.invalid_id': 'Pertanyaan {0} memiliki id yang tidak valid.',
  'def.duplicate_id': 'Pertanyaan {0} memakai ulang id "{1}".',
  'def.needs_title': 'Pertanyaan {0} belum memiliki judul.',
  'def.bad_question_code': 'Kode pertanyaan {0} tidak valid. Gunakan 1–32 huruf, angka, tanda hubung, atau garis bawah.',
  'def.duplicate_question_code': 'Pertanyaan {0} memakai ulang kode "{1}".',
  'def.tooltip_too_long': 'Tooltip info pada pertanyaan {0} lebih dari {1} karakter.',
  'def.bad_align': 'Perataan teks pada pertanyaan {0} tidak dikenal.',
  'def.rich_too_long': 'Teks berformat pada pertanyaan {0} lebih dari {1} karakter.',
  'def.needs_two_options': 'Pertanyaan {0} memerlukan minimal dua opsi.',
  'def.bad_option_id': 'Pertanyaan {0} memiliki id opsi yang kosong atau ganda.',
  'def.option_label': 'Pertanyaan {0} memiliki opsi tanpa label.',
  'def.bad_media': 'Pertanyaan {0} memiliki gambar atau video dengan tautan tidak valid.',
  'def.bad_option_image': 'Pertanyaan {0} memiliki gambar opsi dengan tautan tidak valid.',
  'def.rating_scale': 'Pertanyaan {0} memerlukan skala penilaian 2 hingga 11 poin.',
  'def.min_gt_max': 'Pertanyaan {0} memiliki nilai minimum lebih besar dari maksimum.',
  'def.minlen_gt_maxlen': 'Pertanyaan {0} memiliki panjang minimum lebih besar dari panjang maksimum.',
  'def.minsel_gt_maxsel': 'Pertanyaan {0} memiliki jumlah pilihan minimum lebih besar dari maksimum.',
  'def.condition_earlier': 'Kondisi pada pertanyaan {0} harus merujuk ke pertanyaan sebelumnya.',
  'def.bad_operator': 'Pertanyaan {0} memiliki operator kondisi yang tidak didukung "{1}".',
  'def.condition_no_value': 'Kondisi pada pertanyaan {0} belum memiliki nilai.',
  'def.jump_target_later': 'Pertanyaan {0} harus melompat ke pertanyaan setelahnya.',
  'def.bad_match': 'Pertanyaan {0} memiliki grup kondisi yang harus "semua" atau "salah satu".',
  'def.needs_field': 'Pertanyaan {0} memerlukan minimal satu kolom isian.',
  'def.needs_row': 'Pertanyaan {0} memerlukan minimal satu baris.',
  'def.other_not_allowed': 'Opsi “Lainnya” hanya untuk pertanyaan pilihan tunggal, pilihan ganda, atau dropdown (pertanyaan {0}).',
  'def.one_other': 'Pertanyaan {0} hanya boleh memiliki satu opsi “Lainnya”.',
  'def.bad_score': 'Pertanyaan {0} memiliki opsi dengan poin yang tidak valid.',
  'def.slider_scale': 'Pertanyaan {0} memerlukan rentang slider dengan minimum di bawah maksimum, langkah positif, dan maksimal 1000 langkah.',
  'def.bad_total': 'Pertanyaan {0} memerlukan total lebih dari nol.',
  'def.bad_carry_forward': 'Pertanyaan {0} harus membawa opsi dari pertanyaan pilihan tunggal, pilihan ganda, atau dropdown sebelumnya.',
  'def.carry_forward_type': 'Pertanyaan {0} hanya dapat membawa opsi jika berupa pilihan tunggal, pilihan ganda, dropdown, atau urutan pilihan.',
  'def.bad_condition_row': 'Pertanyaan {0} memiliki kondisi pada baris atau kolom yang tidak ada.',
  'def.jump_ending': 'Pertanyaan {0} melompat ke akhir survei yang tidak ada.',
  'def.bad_ending_id': 'Ada akhir survei dengan id kosong atau ganda.',
  'def.ending_name': 'Setiap akhir survei memerlukan nama.',
  'def.ending_redirect': 'Akhir survei “{0}” memerlukan tautan https untuk pengalihan.',
  'def.bad_hidden_field': 'Kolom tersembunyi “{0}” harus diawali huruf, hanya berisi huruf, angka, dan garis bawah, serta unik.',
  'randomizer.bad_id': 'Sebuah pengacak tidak memiliki id atau id-nya ganda.',
  'randomizer.limit': 'Pengacak {0} memiliki batas yang tidak valid untuk "{1}": gunakan bilangan bulat 1 atau lebih untuk salah satu bagiannya.',
  'randomizer.reset': 'Pengacak {0} memiliki waktu reset yang tidak valid.',
  'randomizer.count': 'Pengacak {0} memerlukan minimal dua bagian dan harus menampilkan 1 sampai {1} bagian.',
  'randomizer.section_missing': 'Pengacak {0} memuat bagian “{1}” yang tidak ada.',
  'randomizer.overlap': 'Bagian “{1}” sudah ada di pengacak lain (pengacak {0}).',
  'randomizer.contiguous': 'Bagian dalam pengacak {0} harus berurutan di kuesioner, tanpa bagian lain di antaranya.',
  'randomizer.jump': 'Pertanyaan {0} melompat ke dalam atau melintasi bagian pengacak, sehingga tujuannya mungkin tidak ditampilkan ke semua responden.',
  'randomizer.condition': 'Pertanyaan {0} bergantung pada pertanyaan di bagian pengacak yang tidak dilihat semua responden.',
  'def.bad_loop': 'Bagian “{0}” harus mengulang berdasarkan pertanyaan pilihan ganda sebelumnya di luar bagian tersebut.',
  'def.bad_quota_id': 'Ada kuota dengan id kosong atau ganda.',
  'def.quota_name': 'Setiap kuota memerlukan nama.',
  'def.quota_limit': 'Kuota “{0}” memerlukan batas minimal 1.',
  'def.quota_conditions': 'Kuota “{0}” memerlukan kondisi yang valid pada pertanyaan di luar perulangan.',
  'def.bad_language': '“{0}” bukan bahasa tambahan yang valid.',
  'def.bad_color': 'Warna tema harus berformat #rrggbb.',
  'def.bad_logo': 'Logo tema memiliki tautan yang tidak valid.',
  'def.bad_font': 'Huruf tema tidak didukung.',
  'def.bad_zoom': 'Ukuran tampilan tema tidak didukung.',
  'survey.needs_title': 'Beri judul pada kuesioner.',
  'a11y.image_alt': 'Pertanyaan {0} memiliki gambar tanpa teks alternatif. Deskripsikan gambar untuk pengguna pembaca layar.',
  'a11y.video_caption': 'Pertanyaan {0} memiliki video tanpa keterangan atau deskripsi. Tambahkan, dan pastikan video memiliki subtitel.',
  'a11y.autoplay_sound': 'Pertanyaan {0} memutar video bersuara secara otomatis. Bisukan atau matikan putar otomatis.',
  'a11y.option_image_label': 'Pertanyaan {0} memiliki opsi bergambar tanpa label teks. Beri label pada setiap opsi.',
  'a11y.matrix_wide': 'Pertanyaan {0} adalah matriks dengan {1} kolom, sulit dipakai di ponsel dan dengan pembaca layar. Batasi hingga 7 kolom.',
  'a11y.matrix_long': 'Pertanyaan {0} adalah matriks dengan {1} pernyataan. Bagi menjadi kelompok yang lebih kecil.',
  'a11y.ranking_long': 'Pertanyaan {0} meminta mengurutkan lebih dari 10 opsi, yang memerlukan banyak langkah dengan papan ketik.',
  'a11y.scale_labels': 'Pertanyaan {0} memiliki skala tanpa label ujung. Jelaskan arti nilai terendah dan tertinggi.',
  'a11y.many_options': 'Pertanyaan {0} memiliki lebih dari 15 opsi. Pertimbangkan dropdown atau membaginya.',
  'a11y.vague_link': 'Pertanyaan {0} memakai “klik di sini”. Jelaskan tujuan tautannya.',
  'a11y.all_caps': 'Pertanyaan {0} ditulis dengan huruf kapital, lebih sulit dibaca dan dapat dieja oleh pembaca layar.',
  'a11y.signature_required': 'Pertanyaan {0} mewajibkan tanda tangan gambar. Sebagian responden tidak dapat menggambarnya; pertimbangkan menjadikannya opsional.',
  'a11y.location_required': 'Pertanyaan {0} mewajibkan lokasi responden. Responden yang tidak dapat atau tidak mau membagikannya akan terhenti; pertimbangkan menjadikannya opsional.',
  'a11y.long_page': 'Semua {0} pertanyaan berada di satu halaman. Bagi kuesioner panjang menjadi beberapa halaman atau bagian.',
  'a11y.timer': 'Penghitung waktu dapat membuat responden tertekan. Tampilkan hanya jika waktu penting.',
  'a11y.accent_contrast': 'Warna aksen memiliki kontras {0}:1 terhadap putih. Gunakan warna lebih gelap (minimal 3:1) agar jawaban terpilih terlihat.',
  'a11y.background_contrast': 'Latar halaman memiliki kontras {0}:1 dengan teks. Gunakan latar lebih terang (minimal 4,5:1).',

  'type.short_text': 'Teks singkat',
  'type.short_text.hint': 'Jawaban satu baris',
  'type.long_text': 'Teks panjang',
  'type.long_text.hint': 'Jawaban berupa paragraf',
  'type.single_choice': 'Pilihan tunggal',
  'type.single_choice.hint': 'Pilih satu opsi',
  'type.multiple_choice': 'Pilihan ganda',
  'type.multiple_choice.hint': 'Pilih beberapa opsi',
  'type.ranking': 'Urutan pilihan',
  'type.ranking.hint': 'Urutkan pilihan dari paling disukai',
  'type.dropdown': 'Dropdown',
  'type.dropdown.hint': 'Pilih satu dari daftar',
  'type.number': 'Angka',
  'type.number.hint': 'Jawaban berupa angka',
  'type.rating': 'Penilaian',
  'type.rating.hint': 'Skala, mis. 1–5',
  'type.date': 'Tanggal',
  'type.date.hint': 'Tanggal kalender',
  'type.yes_no': 'Ya / Tidak',
  'type.yes_no.hint': 'Jawaban dua pilihan',
  'type.matrix': 'Tabel matriks',
  'type.matrix.hint': 'Beberapa pernyataan dengan satu skala',
  'type.slider': 'Slider',
  'type.slider.hint': 'Geser ke sebuah nilai',
  'type.nps': 'Net Promoter Score',
  'type.nps.hint': 'Kemungkinan merekomendasikan, 0–10',
  'type.constant_sum': 'Jumlah tetap',
  'type.constant_sum.hint': 'Bagi sebuah total ke beberapa item',
  'type.text_form': 'Formulir teks',
  'type.text_form.hint': 'Beberapa kotak teks berlabel',
  'type.file_upload': 'Unggah berkas',
  'type.file_upload.hint': 'Foto atau dokumen',
  'type.signature': 'Tanda tangan',
  'type.signature.hint': 'Gambar tanda tangan',
  'type.location': 'Lokasi',
  'type.location.hint': 'Koordinat GPS',
  'type.descriptive': 'Teks / petunjuk',
  'type.descriptive.hint': 'Hanya informasi, tanpa jawaban',

  'op.equals': 'adalah',
  'op.not_equals': 'bukan',
  'op.contains': 'mengandung',
  'op.not_contains': 'tidak mengandung',
  'op.gt': 'lebih dari',
  'op.gte': 'minimal',
  'op.lt': 'kurang dari',
  'op.lte': 'maksimal',
  'op.answered': 'sudah dijawab',
  'op.not_answered': 'belum dijawab',

  yes: 'Ya',
  no: 'Tidak',
  signed: 'Ditandatangani',
};

export const coreMessages: Record<Locale, Record<CoreMessageKey, string>> = { en, id };

/** Renders a core message, replacing {0}, {1}, … with args. */
export function coreT(locale: Locale, key: CoreMessageKey, ...args: (string | number)[]): string {
  const template = coreMessages[locale]?.[key] ?? en[key];
  return template.replace(/\{(\d+)\}/g, (_, i) => String(args[Number(i)] ?? ''));
}

export function questionTypeLabel(type: QuestionType, locale: Locale): string {
  return coreT(locale, `type.${type}` as CoreMessageKey);
}

export function questionTypeHint(type: QuestionType, locale: Locale): string {
  return coreT(locale, `type.${type}.hint` as CoreMessageKey);
}

export function operatorLabel(op: Operator, locale: Locale): string {
  return coreT(locale, `op.${op}` as CoreMessageKey);
}
